'use client';

/**
 * Live insets for the "personal-route" reference screen (canvas 1672×941).
 *
 * The seven profile sliders in the left column become live (7 stops each,
 * knob translates), the "next step" callout and the "why the next step
 * changed" band derive from `profile`, and the compare card toggles
 * `compareRoute`. The painted route, islands and Alexey stay untouched.
 */

import { Fragment, useRef, type CSSProperties } from 'react';
import type { ReferenceDemoState } from '../../../reference/types';
import { rectStyle, SCENE_CANVAS, type Rect } from '../../types';
import type { InsetProps } from '../index';
import styles from './personal-route.module.css';

const C = SCENE_CANVAS['personal-route'];
type Profile = ReferenceDemoState['profile'];
type Field = keyof Profile;
type Pair = { ru: string; en: string };

const FIELDS: readonly { key: Field; label: Pair; low: Pair; high: Pair }[] = [
  { key: 'knowledge', label: { ru: 'Уровень знаний', en: 'Knowledge level' }, low: { ru: 'база', en: 'basics' }, high: { ru: 'уверенно', en: 'confident' } },
  { key: 'goal', label: { ru: 'Цель', en: 'Goal' }, low: { ru: 'разобраться', en: 'understand' }, high: { ru: 'сдать на высокий', en: 'top grade' } },
  { key: 'pace', label: { ru: 'Темп', en: 'Pace' }, low: { ru: 'спокойный', en: 'calm' }, high: { ru: 'быстрый', en: 'fast' } },
  { key: 'confidence', label: { ru: 'Уверенность', en: 'Confidence' }, low: { ru: 'низкая', en: 'low' }, high: { ru: 'высокая', en: 'high' } },
  { key: 'time', label: { ru: 'Доступное время', en: 'Available time' }, low: { ru: 'мало', en: 'little' }, high: { ru: 'много', en: 'plenty' } },
  { key: 'mistakes', label: { ru: 'Типичные ошибки', en: 'Typical errors' }, low: { ru: 'по невнимательности', en: 'slips' }, high: { ru: 'по теме', en: 'topic gaps' } },
  { key: 'motivation', label: { ru: 'Мотивация', en: 'Motivation' }, low: { ru: 'нужен пинок', en: 'needs a push' }, high: { ru: 'внутренняя', en: 'intrinsic' } },
];

const ROW_Y = [241, 331, 421, 511, 601, 691, 781] as const;
const STOP_X0 = 71;
const STOP_STEP = 32.5;
const STOP_W = 33;
const STOP_H = 42;
/** Opaque cover over the painted track + knob glow (a little taller than the 18 px strip). */
const STRIP = (y: number): Rect => [76, y + 14, 224, 24];
const KNOB = 15;
/** Knob left inside the strip for value 0: centre 87.5 → 87.5 − 76 − 7.5. */
const KNOB_X0 = 87.5 - 76 - KNOB / 2;
const LEVELS = 7;

const CALLOUT: Rect = [716, 217, 211, 124];
const REASONS: Rect = [382, 730, 1000, 120];
const SENTENCE: Rect = [382, 825, 700, 30];
const COMPARE_CARD: Rect = [1387, 711, 229, 154];
const COMPARE_BUTTON: Rect = [32, 862, 262, 48];

type StepKind = 'base' | 'source' | 'next' | 'default';

interface NextStep {
  kind: StepKind;
  title: Pair;
  why: Pair;
}

/** Deterministic and small: which painted route node comes next, and one line why. */
function nextStep(p: Profile): NextStep {
  if (p.knowledge <= 1) {
    return { kind: 'base', title: { ru: 'Линейные уравнения', en: 'Linear equations' }, why: { ru: 'Сначала вернём основу: без неё квадратные уравнения не держатся', en: 'First restore the base: without it quadratics do not hold' } };
  }
  if (p.mistakes >= 4) {
    return { kind: 'source', title: { ru: 'Формулы сокращённого умножения', en: 'Special products' }, why: { ru: 'Ошибки идут по теме: закроем их источник до новых задач', en: 'The errors follow the topic: close their source before new tasks' } };
  }
  if (p.knowledge >= 5 && p.confidence >= 4) {
    return { kind: 'next', title: { ru: 'Квадратичная функция', en: 'Quadratic function' }, why: { ru: 'База устойчива: переходим к новой теме', en: 'The base is stable: on to the new topic' } };
  }
  return {
    kind: 'default',
    title: { ru: 'Квадратные уравнения', en: 'Quadratic equations' },
    why: p.time <= 1
      ? { ru: 'Короткий блок: базовые типы за одно занятие', en: 'A short block: the basic types in one session' }
      : { ru: 'Потренируем безошибочное решение базовых типов', en: 'Practise error free solving of the basic types' },
  };
}

function band(value: number): 0 | 1 | 2 {
  return value <= 1 ? 0 : value <= 3 ? 1 : 2;
}

const REASON_COPY: Record<'mistakes' | 'time' | 'confidence' | 'motivation', readonly [Pair, Pair, Pair]> = {
  mistakes: [
    { ru: 'Ошибки редкие и случайные: по невнимательности', en: 'Errors are rare and random: slips' },
    { ru: 'Вы чаще ошибаетесь в базовых типах квадратных уравнений', en: 'You err more often in the basic types of quadratic equations' },
    { ru: 'Ошибки повторяются по теме, а не случайно', en: 'Errors repeat by topic rather than by chance' },
  ],
  time: [
    { ru: 'Доступного времени на этой неделе заметно меньше', en: 'Noticeably less time available this week' },
    { ru: 'Доступного времени на этой неделе меньше, чем обычно', en: 'Less time available this week than usual' },
    { ru: 'Времени достаточно для длинного блока', en: 'Enough time for a longer block' },
  ],
  confidence: [
    { ru: 'Уверенность низкая: нужны опора и быстрый успех', en: 'Confidence is low: support and a quick win are needed' },
    { ru: 'Уверенность немного снизилась после прошлой темы', en: 'Confidence dipped a little after the last topic' },
    { ru: 'Уверенность высокая: можно повышать сложность', en: 'Confidence is high: difficulty can rise' },
  ],
  motivation: [
    { ru: 'Мотивация низкая: начнём с самого короткого шага', en: 'Motivation is low: start with the shortest step' },
    { ru: 'Мотивация средняя: чередуем практику и объяснение', en: 'Motivation is moderate: alternate practice and explanation' },
    { ru: 'Мотивация высокая: используем её для быстрого прогресса', en: 'Motivation is high: we use it for fast progress' },
  ],
};

const SENTENCE_COPY: Record<StepKind, Pair> = {
  base: { ru: 'Поэтому мы возвращаемся к основе и даём короткую проверку.', en: 'So we return to the base and give a short check.' },
  source: { ru: 'Поэтому мы закрываем источник ошибок перед новой темой.', en: 'So we close the source of errors before the new topic.' },
  next: { ru: 'Поэтому мы открываем новую тему и повышаем сложность.', en: 'So we open the new topic and raise the difficulty.' },
  default: { ru: 'Поэтому мы усиливаем базу и даём целенаправленную практику.', en: 'So we strengthen the base and give targeted practice.' },
};

const T = {
  profile: { ru: 'Профиль ученика', en: 'Learner profile' },
  level: { ru: 'уровень', en: 'level' },
  of: { ru: 'из', en: 'of' },
  nextStep: { ru: 'Следующий шаг', en: 'Next step' },
  nextLabel: { ru: 'Следующий шаг', en: 'Next step' },
  why: { ru: 'Почему следующий шаг изменился', en: 'Why the next step changed' },
  more: { ru: 'Подробнее', en: 'Details' },
  moreSeed: { ru: 'Почему маршрут изменился?', en: 'Why did the route change?' },
  compare: { ru: 'Сравнить маршрут с Марией', en: 'Compare route with Maria' },
  compareOn: { ru: 'сравнение включено: маршрут Марии', en: 'comparison on: Maria’s route' },
} as const;

function useChanged(value: unknown): boolean {
  const initial = useRef(value);
  const changed = useRef(false);
  if (!Object.is(value, initial.current)) changed.current = true;
  return changed.current;
}

function ReasonIcon({ kind }: { kind: keyof typeof REASON_COPY }) {
  switch (kind) {
    case 'mistakes':
      return <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" /><circle cx="12" cy="12" r="2" fill="currentColor" /><path d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>;
    case 'time':
      return <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M12 7v5.5l3.5 2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    case 'confidence':
      return <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3 5 6v6c0 4.2 3 7.6 7 9 4-1.4 7-4.8 7-9V6z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /><path d="m9 12 2 2 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    default:
      return <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m12 3 2.7 5.6 6.1.8-4.5 4.3 1.1 6.1L12 17l-5.4 2.8 1.1-6.1L3.2 9.4l6.1-.8z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>;
  }
}

export default function PersonalRouteInsets({ lang, state, update, navigate, openAssistant, spotlight, onInteraction }: InsetProps) {
  const pick = <V,>(pair: { ru: V; en: V }): V => (lang === 'ru' ? pair.ru : pair.en);
  const profile = state.profile;
  const step = nextStep(profile);
  const profileKey = FIELDS.map((f) => profile[f.key]).join('');
  const live = useChanged(profileKey);
  const compareLive = useChanged(state.compareRoute);
  const isSpot = (id: string) => spotlight === id;
  const act = (label: string, run: () => void) => () => {
    onInteraction?.(label);
    run();
  };

  const reasons = (['mistakes', 'time', 'confidence', 'motivation'] as const).map((kind) => ({ kind, text: pick(REASON_COPY[kind][band(profile[kind])]) }));
  const compareLabel = pick(T.compare);

  return (
    <div className={styles.root}>
      {/* ---------- sliders ---------- */}
      <div className={styles.sliders} role="group" aria-label={pick(T.profile)}>
        {FIELDS.map((field, row) => {
          const value = Math.min(LEVELS - 1, Math.max(0, profile[field.key]));
          const knobStyle: CSSProperties & Record<'--v', string> = {
            left: `${(KNOB_X0 / STRIP(0)[2]) * 100}%`,
            width: `${(KNOB / STRIP(0)[2]) * 100}%`,
            '--v': `${value}`,
          };
          return (
            <Fragment key={field.key}>
              <span className={styles.strip} style={rectStyle(STRIP(ROW_Y[row]), C)} aria-hidden="true">
                <i className={styles.track} />
                {Array.from({ length: LEVELS }, (_, v) => (
                  <i key={v} className={styles.dot} style={{ left: `${((KNOB_X0 + KNOB / 2 + v * STOP_STEP) / STRIP(0)[2]) * 100}%` }} />
                ))}
                <i className={styles.knob} style={knobStyle} />
              </span>
              {Array.from({ length: LEVELS }, (_, v) => {
                const id = `profile-${field.key}-${v}`;
                const label = `${pick(field.label)}: ${pick(T.level)} ${v} ${pick(T.of)} ${LEVELS - 1}${v === 0 ? ` (${pick(field.low)})` : v === LEVELS - 1 ? ` (${pick(field.high)})` : ''}`;
                return (
                  <button
                    key={id}
                    type="button"
                    className={styles.stop}
                    style={rectStyle([STOP_X0 + v * STOP_STEP, ROW_Y[row], STOP_W, STOP_H], C)}
                    data-hotspot={id}
                    data-spotlight={isSpot(id) ? 'true' : undefined}
                    aria-pressed={v === value}
                    aria-label={label}
                    onClick={act(label, () => update({ profile: { [field.key]: v } }))}
                  >
                    {isSpot(id) && <i className={styles.pulse} aria-hidden="true" />}
                  </button>
                );
              })}
            </Fragment>
          );
        })}
      </div>

      {/* ---------- next step callout ---------- */}
      <button
        type="button"
        className={styles.callout}
        style={rectStyle(CALLOUT, C)}
        data-hotspot="route-next"
        data-live={live || undefined}
        data-spotlight={isSpot('route-next') ? 'true' : undefined}
        aria-label={`${pick(T.nextLabel)}: ${pick(step.title)}`}
        onClick={act(`${pick(T.nextLabel)}: ${pick(step.title)}`, () => navigate('learning-cycle'))}
      >
        <span key={step.title.ru + step.why.ru} className={styles.calloutBody}>
          <small style={{ '--i': 0 } as CSSProperties}>{pick(T.nextStep)}</small>
          <b style={{ '--i': 1 } as CSSProperties}>{pick(step.title)}</b>
          <span style={{ '--i': 2 } as CSSProperties}>{pick(step.why)}</span>
        </span>
        <i key={`relight-${step.title.ru}`} className={styles.relight} aria-hidden="true" />
        {isSpot('route-next') && <i className={styles.pulse} aria-hidden="true" />}
      </button>

      {/* ---------- why the next step changed ---------- */}
      <section className={styles.reasons} style={rectStyle(REASONS, C)} data-live={live || undefined} aria-label={pick(T.why)}>
        <div key={profileKey} className={styles.reasonRow}>
          {reasons.map((reason, index) => (
            <Fragment key={reason.kind}>
              {index > 0 && <span className={styles.reasonArrow} aria-hidden="true">›</span>}
              <div className={styles.reason} style={{ '--i': index } as CSSProperties}>
                <span className={styles.reasonIcon}><ReasonIcon kind={reason.kind} /></span>
                <p>{reason.text}</p>
              </div>
            </Fragment>
          ))}
        </div>
        <i key={`relight-${profileKey}`} className={styles.relight} aria-hidden="true" />
      </section>

      <div className={styles.sentence} style={rectStyle(SENTENCE, C)} data-live={live || undefined}>
        <p key={step.kind}>
          {pick(SENTENCE_COPY[step.kind])}
          <button type="button" className={styles.more} aria-label={pick(T.more)} onClick={act(pick(T.more), () => openAssistant(pick(T.moreSeed)))}>
            {pick(T.more)} <span aria-hidden="true">›</span>
          </button>
        </p>
      </div>

      {/* ---------- compare ---------- */}
      <button
        type="button"
        className={styles.compare}
        style={rectStyle(COMPARE_CARD, C)}
        data-hotspot="route-compare-card"
        data-on={state.compareRoute ? 'true' : undefined}
        data-live={compareLive || undefined}
        data-spotlight={isSpot('route-compare-card') ? 'true' : undefined}
        aria-pressed={state.compareRoute}
        aria-label={compareLabel}
        onClick={act(compareLabel, () => update({ compareRoute: !state.compareRoute }))}
      >
        {state.compareRoute && (
          <span className={styles.compareChip}>
            <i aria-hidden="true" />
            {pick(T.compareOn)}
          </span>
        )}
        {isSpot('route-compare-card') && <i className={styles.pulse} aria-hidden="true" />}
      </button>
      <i className={styles.compareRing} style={rectStyle(COMPARE_BUTTON, C)} data-on={state.compareRoute ? 'true' : undefined} aria-hidden="true" />
    </div>
  );
}
